import { gql } from "@apollo/client";
import CsvImportDialog from "controls/CsvImportDialog";
import { csvRequiredColumnsMessage, csvToAssetUploader } from "controls/CsvPreviewTable";
import Message from "controls/Message";
import { useActionFragment } from "hooks/useAction";
import useData from "hooks/useData";
import { FileUpload } from "mdi-material-ui";
import React from "react";

const IMPORT_PROFILES_CSV_HEADERS = ["Profile ID", "Account Holder Email"];

const EXAMPLE_PROFILE_ID = "12345678-abcd-1234-abcd-123456789abc";
const PROFILE_ID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function downloadTemplate() {
  const csv = [IMPORT_PROFILES_CSV_HEADERS.join(","), [EXAMPLE_PROFILE_ID, "john@example.com"].join(",")].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "import_profiles_template.csv";
  a.click();
  URL.revokeObjectURL(url);
}

const COLUMN_VALIDATORS = {
  "Profile ID": (v) => {
    if (!v.trim()) return "Cannot be empty";
    if (!PROFILE_ID_REGEX.test(v.trim())) return `Must be a valid UUID (e.g. ${EXAMPLE_PROFILE_ID})`;
    return null;
  },
  "Account Holder Email": (v) => {
    if (!v.trim()) return "Cannot be empty";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())) return "Must be a valid email address";
    return null;
  },
};

ImportPeopleDialog.useButtonProps = ({ orgId }) => {
  const [data] = useData(
    gql`
      query ImportPeopleDialog_ButtonProps($orgId: ID!) {
        org(id: $orgId) {
          id
          isPlayhqIntegrated
        }
      }
    `,
    { orgId },
    { skip: !orgId },
  );

  if (!orgId || !data?.org.isPlayhqIntegrated) return null;
  return { icon: <FileUpload />, content: "Connect Profiles" };
};

export default function ImportPeopleDialog({ onSubmit, onClose }) {
  const importPeople = useActionFragment("importPeople", "report { id imported importErrorMessage }");

  const handleSubmit = async (csvHeaders, csvRows, uploaderValue) => {
    const assetUploader = csvToAssetUploader(csvHeaders, csvRows, uploaderValue.name);
    const result = await importPeople({ input: { assetUploader } }, { suppressError: true });
    await onSubmit?.(result.report);
  };

  return (
    <CsvImportDialog
      title="Connect Profiles"
      infoMessage={
        <>
          Upload a CSV file to connect PlayHQ participant email addresses to their profiles.{" "}
          {csvRequiredColumnsMessage(IMPORT_PROFILES_CSV_HEADERS)}
        </>
      }
      columnValidators={COLUMN_VALIDATORS}
      requiredHeaders={IMPORT_PROFILES_CSV_HEADERS}
      onDownloadTemplate={downloadTemplate}
      onSubmit={handleSubmit}
      renderSuccess={
        <Message
          type="success"
          content="The CSV file has been uploaded successfully. The system will continue processing the file in the background."
        />
      }
      onClose={onClose}
    />
  );
}
